/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import getInvoicePdf from "@eisinc/App/Handlers/GetInvoicePdf";
import LoadingSpinner, { LoadingSpinnerProps } from "@eisinc/MobiusOverrides/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import { InvoicePaymentModel } from "@eisinc/XDL/interfaces";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    getInvoicePdf,
};

export interface PDFLinkCellStyles {
    invoiceItemNumberDataCellLink?: LinkPresentationProps;
    invoiceItemNumberNonCredit?: TypographyPresentationProps;
    modalPDFWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const pdfLinkCellStyles: PDFLinkCellStyles = {};

interface PDFLinkCellProps {
    invoice: InvoicePaymentModel;
    showOriginalInvoiceNumber?: boolean;
}

export interface InvoicesParam {
    invoicePdf: string;
}

type Props = PDFLinkCellProps & ResolveThunks<typeof mapDispatchToProps>;

const PDFLinkCell: React.FunctionComponent<Props> = ({ invoice, getInvoicePdf, showOriginalInvoiceNumber }) => {
    const toasterContext = React.useContext(ToasterContext);
    const styles = pdfLinkCellStyles;
    const { invoiceAmount, invoiceNumber, originalInvoiceNumber } = invoice;

    const [isLoading, setIsLoading] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [pdfInvoices, setPdfInvoices] = React.useState([""]);
    const [selectedInvoicePDF, setSelectedInvoicePDF] = React.useState("");

    const resetPDFData = () => {
        setSelectedInvoicePDF("");
        setIsLoading(false);
    };

    const handlePDFDownload = (invoiceNumber: string, invoice?: InvoicesParam) => {
        const pdfData = invoice?.invoicePdf;

        if (!pdfData) {
            return;
        }
        const fileName = `invoice ${invoiceNumber}.pdf`;
        const dataUri = `data:application/pdf;base64,${pdfData}`;
        const link = document.createElement("a");
        link.href = dataUri;
        link.target = "_blank";
        link.download = fileName;

        document.body.appendChild(link);

        const clickEvent = new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: false,
        });
        link.dispatchEvent(clickEvent);

        document.body.removeChild(link);
    };

    const handlePDFClick = (invoiceNumber: string) => {
        setIsLoading(true);
        setSelectedInvoicePDF(invoiceNumber);
        getInvoicePdf({
            invoiceNumber,
            onSuccess: (response: any) => {
                setPdfInvoices(response.invoices);
                if (response.invoices.length > 1) {
                    setIsModalOpen(true);
                } else {
                    handlePDFDownload(invoiceNumber, response?.invoices?.[0]);
                    resetPDFData();
                }
            },
            onError: (error: any) => {
                resetPDFData();
                toasterContext.addToast({ body: translate("No Available PDF file"), messageType: "danger" });
            },
        });
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
        setSelectedInvoicePDF("");
    };

    return (
        <>
            {(selectedInvoicePDF === invoiceNumber || selectedInvoicePDF === originalInvoiceNumber) && isLoading ? (
                <LoadingSpinner {...styles.spinner} />
            ) : invoiceAmount > 0 || showOriginalInvoiceNumber ? (
                <Link
                    data-test-selector="invoicePaymentTable_invoiceItemNumber"
                    onClick={() => handlePDFClick(showOriginalInvoiceNumber ? originalInvoiceNumber : invoiceNumber)}
                    {...styles.invoiceItemNumberDataCellLink}
                >
                    {showOriginalInvoiceNumber ? originalInvoiceNumber : invoiceNumber}
                </Link>
            ) : (
                <Typography {...styles.invoiceItemNumberNonCredit}>{invoiceNumber}</Typography>
            )}

            <Modal
                headline={siteMessage("Custom_InvoicesPDF_Modal_Title", "Invoices PDF")}
                size={200}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
            >
                {pdfInvoices.map((invoiceFile: any, index) => (
                    <StyledWrapper key={index} {...styles.modalPDFWrapper}>
                        <Link
                            as="a"
                            href={`data:application/pdf;base64,${invoiceFile.invoicePdf}`}
                            download={`invoice ${selectedInvoicePDF}_${index + 1}.pdf`}
                            onClick={modalCloseHandler}
                        >
                            {translate(`invoice ${selectedInvoicePDF}_${index + 1}.pdf`)}
                        </Link>
                    </StyledWrapper>
                ))}
            </Modal>
        </>
    );
};

export default connect(null, mapDispatchToProps)(PDFLinkCell);
